<template>
    <div v-if="productPageStore.lipscoreProduct" class="flex flex-col gap-4">
        <div class="">
            <ReviewStars :review-score="productPageStore.lipscoreProduct.rating" size="md" show-rating show-score />
            <span class="text-sm text-slate-500">
                {{
                    t('general.reviews.review_count', {
                        votes: productPageStore.lipscoreProduct.votes,
                        reviewCount: productPageStore.lipscoreProduct.review_count,
                    })
                }}
            </span>
        </div>

        <!-- Subgrid keeps one shared track per column, so every bar ends at the same x regardless of count length. -->
        <div class="grid grid-cols-[60px_1fr_auto] gap-x-2 gap-y-2 sm:grid-cols-[85px_1fr_auto] sm:gap-x-3">
            <div
                v-for="rating in starRatings"
                :key="`rating_bar_${rating.score}`"
                class="col-span-3 grid grid-cols-subgrid items-center text-left text-xs sm:text-sm"
            >
                <span class="min-w-0 truncate">{{ t('general.reviews.filter_stars', rating.score) }}</span>

                <div class="h-2 w-full min-w-0 overflow-hidden rounded-full bg-gray-100">
                    <div class="h-2 rounded-full bg-review-star-highlight" :style="{ width: `${percentageFor(rating.count)}%` }" />
                </div>

                <span class="text-right whitespace-nowrap text-slate-500"> {{ t('general.reviews.review_amount', rating.count ?? 0) }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ReviewStars from '@/components/reviews/ReviewStars.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import _ from 'lodash';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const productPageStore = useProductPageStore();

const starRatings = computed(() =>
    productPageStore.lipscoreProduct
        ? _.orderBy(
              [
                  { score: 1.0, count: productPageStore.lipscoreProduct.one_star_rating_count },
                  { score: 2.0, count: productPageStore.lipscoreProduct.two_star_rating_count },
                  { score: 3.0, count: productPageStore.lipscoreProduct.three_star_rating_count },
                  { score: 4.0, count: productPageStore.lipscoreProduct.four_star_rating_count },
                  { score: 5.0, count: productPageStore.lipscoreProduct.five_star_rating_count },
              ],
              'score',
              'desc',
          )
        : [],
);

// Bars must be relative to the star counts they render, not to votes — votes is far higher.
const totalRatingCount = computed((): number =>
    _.sumBy(starRatings.value, (starRating: { score: number; count?: number | null }): number => starRating.count ?? 0),
);

const percentageFor = (count?: number | null): number => {
    if (!totalRatingCount.value || !count) {
        return 0;
    }

    return Math.round((count / totalRatingCount.value) * 100);
};
</script>
